import { useEffect } from 'react';

/**
 * Moves focus to the current screen's `h1` when `key` changes — the SPA equivalent of a page load.
 *
 * A `MutationObserver` is required because several screens are `lazyWithRetry`: the first paint after
 * a view-state change is still the resolving skeleton's `h1`, and focusing that would leave the
 * real heading unfocused once the chunk lands. We re-focus only when the `h1` *node* is replaced, so
 * countdown and poll ticks cannot steal focus.
 *
 * The heading is not interactive. `#main-content h1:focus` / `:focus-visible` in `global.css`
 * uses a transparent outline so WebKit does not paint its platform focus ring.
 */
export function useFocusOnChange(key: string): void {
  useEffect(() => {
    const root = document.getElementById('main-content');
    if (root === null) return;

    let lastHeading: HTMLElement | null = null;
    const focusIfNew = () => {
      const heading = root.querySelector('h1');
      if (!(heading instanceof HTMLElement) || heading === lastHeading) return;
      lastHeading = heading;
      heading.focus();
    };

    focusIfNew();
    const observer = new MutationObserver(focusIfNew);
    observer.observe(root, { childList: true, subtree: true });
    return () => { observer.disconnect(); };
  }, [key]);
}
